import moment from 'moment/moment'
import { useNavigate } from 'react-router-dom'

import { Booking } from '../../models/Booking'
import { Gift } from '../../models/Gift'
import { Invoice } from '../../models/Invoice'
import { Item } from '../../models/Item'
import { Package } from '../../models/Package'
import { Transaction } from '../../models/Transaction'
import { User } from '../../models/User'
import { API_BASE } from '../../utils/constants'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    gift: Gift
    isWorkflowReviewStep: boolean
    client: User
    giftItem: Item
    purchasedTransactions?: Transaction[]
}

export const GiftView = ({
    gift,
    isWorkflowReviewStep,
    client,
    giftItem,
    purchasedTransactions,
}: Props) => {
    const navigate = useNavigate()

    const expiresAfter =
        gift.expires === 360
            ? '1 year '
            : gift.expires === 720
              ? '2 years'
              : gift.expires === 1800
                ? '5 years'
                : undefined

    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center">
                        {giftItem.image?.uri !== undefined ? (
                            <img src={giftItem.image.uri} className="size-[116px] rounded-full" />
                        ) : (
                            <Icon
                                icon="gift.large"
                                background="round"
                                className="size-[116px]"
                                svgClassName="size-[56px]"
                            />
                        )}

                        <Heading size="sm" className="mt-6 mb-1 text-center">
                            {giftItem.name ?? ''}
                        </Heading>

                        <div className="flex flex-col items-center justify-center gap-y-1">
                            {isWorkflowReviewStep && (
                                <Paragraph size="lg" className="font-normal text-gray-600">
                                    Requires payment
                                </Paragraph>
                            )}
                            <Paragraph size="lg" className="font-normal text-gray-600">
                                {isWorkflowReviewStep
                                    ? expiresAfter === undefined
                                        ? "Doesn't expire"
                                        : `Expires after ${expiresAfter}`
                                    : gift.expiredDate !== undefined
                                      ? `${gift.expiredDate.isBefore() ? 'Expired' : 'Expires'} on ${gift.expiredDate.format('MMM Do, YYYY')}`
                                      : "Doesn't expire"}
                            </Paragraph>
                            <Paragraph size="lg" className="font-normal text-gray-600">
                                {currencyFormat(giftItem.amount ?? 0, true)}
                            </Paragraph>
                            <Paragraph size="lg" className="font-normal text-gray-600">
                                {gift.statusString}
                            </Paragraph>
                        </div>
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-y-6">
                        {!!gift.subaccount && (
                            <BaseRecordView.IconField
                                icon={
                                    <ContactAvatar
                                        user={gift.subaccount}
                                        containerClassName="row-span-2"
                                        hasHoverEffect
                                    />
                                }
                                title={gift.subaccount.name ?? ''}
                                subtitle={gift.subaccount.labelString}
                                href={`/subaccounts/${gift.subaccount.id}`}
                            />
                        )}

                        {gift.client &&
                            gift.client.id !== undefined &&
                            gift.client.id !== client.id && (
                                <BaseRecordView.IconField
                                    icon={
                                        <ContactAvatar
                                            user={gift.client}
                                            hasHoverEffect={!isWorkflowReviewStep}
                                        />
                                    }
                                    title={gift.client.name ?? ''}
                                    subtitle="Paid by"
                                    secondaryView={
                                        !isWorkflowReviewStep ? (
                                            <a href={`/messages/${gift.client.id}`}>
                                                <Button intent="secondary" size="small">
                                                    Chat
                                                </Button>
                                            </a>
                                        ) : undefined
                                    }
                                    href={
                                        !isWorkflowReviewStep
                                            ? `/clients/${gift.client.id}`
                                            : undefined
                                    }
                                />
                            )}

                        <BaseRecordView.IconField
                            icon={
                                <ContactAvatar
                                    user={client}
                                    hasHoverEffect={!isWorkflowReviewStep}
                                />
                            }
                            title={client.name ?? ''}
                            subtitle="Client"
                            secondaryView={
                                !isWorkflowReviewStep ? (
                                    <a href={`/messages/${client.id}`}>
                                        <Button intent="secondary" size="small">
                                            Chat
                                        </Button>
                                    </a>
                                ) : undefined
                            }
                            href={`/clients/${client.id}`}
                        />
                    </div>
                </Card>

                {!isWorkflowReviewStep && (
                    <Card size="sm" title="Status">
                        <RecordStatusChecklist
                            items={gift.statuses.map((status) => ({
                                title: status.title,
                                subtitle: status.date?.format('MMM Do, YYYY [at] h:mm A'),
                                status: status.date ? 'checked' : 'empty',
                            }))}
                            className="mt-6"
                        />
                    </Card>
                )}
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                {!isWorkflowReviewStep && purchasedTransactions !== undefined && (
                    <Card
                        title="Purchases"
                        count={
                            purchasedTransactions.length > 0
                                ? purchasedTransactions.length
                                : undefined
                        }
                        className="flex flex-col items-stretch justify-start gap-y-8"
                    >
                        <div className="flex flex-col items-stretch justify-start gap-y-1 rounded-lg border border-gray-300 px-6 py-4">
                            <Heading size="sm" className="text-gray-700">
                                {currencyFormat(gift.balance, true)}
                            </Heading>
                            <Paragraph size="md" className="text-gray-600">
                                Remaining balance
                            </Paragraph>
                        </div>

                        {purchasedTransactions.length > 0 && (
                            <div className="flex flex-col items-stretch justify-start gap-y-1">
                                {purchasedTransactions.map((transaction) => (
                                    <button
                                        key={transaction.id}
                                        className="relative z-10 flex items-stretch justify-center gap-x-4 rounded-lg text-left before:absolute before:inset-[-16px] before:-z-10 before:hidden before:rounded-lg before:bg-black/4 before:content-[''] hover:before:block"
                                        onClick={() =>
                                            navigate(
                                                transaction instanceof Booking
                                                    ? `/bookings/${transaction.id}`
                                                    : transaction instanceof Invoice
                                                      ? `/invoices/${transaction.id}`
                                                      : transaction instanceof Package
                                                        ? `/packages/${transaction.id}`
                                                        : `/subscriptions/${transaction.id}`
                                            )
                                        }
                                    >
                                        <div className="shrink-0">
                                            {transaction.client ? (
                                                <ContactAvatar user={transaction.client} />
                                            ) : (
                                                <Icon icon="user.large" background="round" />
                                            )}
                                        </div>
                                        <div className="flex grow flex-col items-stretch justify-center gap-y-1">
                                            <Label size="md" className="text-gray-700">
                                                {transaction.client?.name}
                                            </Label>
                                            <Paragraph size="sm" className="text-gray-600">
                                                {moment(transaction.createdDate).format(
                                                    'MMMM DD, YYYY'
                                                )}
                                            </Paragraph>
                                        </div>
                                        <div className="h-full">
                                            <Label size="md" className="text-gray-700">
                                                {currencyFormat(transaction.total, true)}
                                            </Label>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </Card>
                )}

                <Card
                    title="Payment details"
                    className="flex flex-col items-stretch justify-start gap-y-8"
                >
                    <WorkflowPage.ReceiptView
                        receipt={{
                            modifiers: {
                                subtotal: {
                                    amount: giftItem?.rate ?? 0,
                                },
                                discount:
                                    gift.discountAmount !== undefined && gift.discountAmount !== 0
                                        ? {}
                                        : undefined,
                                salesTax: !!gift.tax
                                    ? {
                                          amount: gift.taxAmount,
                                          parenthetical: `${gift.tax}%`,
                                      }
                                    : undefined,
                                gratuity:
                                    gift.gratuityAmount > 0
                                        ? {
                                              amount: gift.gratuityAmount,
                                              percent:
                                                  gift.gratuityType === 'percent'
                                                      ? gift.gratuity
                                                      : undefined,
                                          }
                                        : undefined,
                                surcharge: gift.allowSurcharge
                                    ? {
                                          amount: gift.surchargeAmount,
                                      }
                                    : undefined,
                            },
                            total: gift.chargeAmount,
                            paymentMethodSource: !isWorkflowReviewStep ? gift.source : undefined,
                        }}
                        transaction={gift}
                        modifierGapSize="small"
                    />
                </Card>

                {!isWorkflowReviewStep && (
                    <WorkflowPage.FormAndContractCard
                        form={gift.form}
                        contract={gift.contract}
                        onOpenForm={
                            gift.record ? () => navigate(`/forms/${gift.record?.id}`) : undefined
                        }
                        onOpenContract={
                            gift.signature
                                ? () =>
                                      window.open(`${API_BASE}/gift/${gift.id}/contract`, '_blank')
                                : undefined
                        }
                    />
                )}

                {(gift.memo ?? '').trim().length > 0 && (
                    <Card title="Memo">
                        <Paragraph className="mt-8 whitespace-pre-wrap text-gray-600">
                            {gift.memo}
                        </Paragraph>
                    </Card>
                )}
            </BaseRecordView.RightColumn>
        </BaseRecordView.Container>
    )
}
